import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { BulkPriceDiscriminator, RepositoryRegistry } from '@mews-commander/data-access';
import { BusinessError } from '@mews/framework';
import { getEditValue } from '../../../../utilities';
import { FormValues, PreparedValues } from './types';
import {
    ABSOLUTE_ADJUSTMENT_FIELD_NAME,
    BASE_PRICE_FIELD_NAME,
    BASE_RATE_KEY_FIELD_NAME,
    ENABLED_FIELD_NAME,
    EXTRA_OCCUPANCY_ADJUSTMENT_FIELD_NAME,
    HIDE_PRICE_FROM_GUEST_FIELD_NAME,
    IS_BONUS_POINTS_ELIGIBLE_FIELD_NAME,
    NEGATIVE_OCCUPANCY_ADJUSTMENT_FIELD_NAME,
    ORDERING_FIELD_NAME,
    RATE_KEY_FIELD_NAME,
    RELATIVE_ADJUSTMENT_FIELD_NAME,
} from './fields';
import { FieldName } from './fields/types';

export const keyByLanguageCodes = (languageCodes: string[], value?: string) =>
    value
        ? pipe(
              languageCodes,
              array.map((languageCode) => [languageCode, value]),
              Object.fromEntries
          )
        : undefined;

const priceFieldsKeys = [
    ABSOLUTE_ADJUSTMENT_FIELD_NAME,
    RELATIVE_ADJUSTMENT_FIELD_NAME,
    BASE_PRICE_FIELD_NAME,
    EXTRA_OCCUPANCY_ADJUSTMENT_FIELD_NAME,
    NEGATIVE_OCCUPANCY_ADJUSTMENT_FIELD_NAME,
];
const isPriceFieldsKey = (
    key: keyof FormValues
): key is
    | typeof ABSOLUTE_ADJUSTMENT_FIELD_NAME
    | typeof RELATIVE_ADJUSTMENT_FIELD_NAME
    | typeof EXTRA_OCCUPANCY_ADJUSTMENT_FIELD_NAME
    | typeof NEGATIVE_OCCUPANCY_ADJUSTMENT_FIELD_NAME
    | typeof BASE_PRICE_FIELD_NAME => priceFieldsKeys.includes(key);

const optionsFieldsKeys = [HIDE_PRICE_FROM_GUEST_FIELD_NAME, IS_BONUS_POINTS_ELIGIBLE_FIELD_NAME];
const isOptionsFieldsKey = (
    key: keyof FormValues
): key is typeof HIDE_PRICE_FROM_GUEST_FIELD_NAME | typeof IS_BONUS_POINTS_ELIGIBLE_FIELD_NAME =>
    optionsFieldsKeys.includes(key);

type BulkEditRatesValues = Omit<BulkEditRatesParams, 'rateIds'>;
interface GetBulkEditRatesValuesParameters {
    formValues: FormValues;
    preparedValues: PreparedValues;
    modifiedValuesKeys: Array<keyof FormValues>;
}

export type BulkEditRatesParams = Parameters<RepositoryRegistry['rates']['bulkUpdate']>[0];

export const getBulkEditRatesValues = ({
    formValues,
    preparedValues,
    modifiedValuesKeys,
}: GetBulkEditRatesValuesParameters) =>
    pipe(
        modifiedValuesKeys,
        array.reduce<keyof FormValues, BulkEditRatesValues>({}, (acc, key) => {
            if (isPriceFieldsKey(key)) {
                switch (key) {
                    case BASE_PRICE_FIELD_NAME:
                        if (preparedValues?.price?.discriminator !== BulkPriceDiscriminator.BaseRate) {
                            throw new BusinessError(`"${key}" field was updated for dependent rate`);
                        }

                        acc.price = {
                            discriminator: preparedValues.price.discriminator,
                            value: {
                                ...acc.price?.value,
                                value: {
                                    value: preparedValues.price.value.value,
                                },
                            },
                        };
                        break;
                    case EXTRA_OCCUPANCY_ADJUSTMENT_FIELD_NAME:
                        if (preparedValues?.price?.discriminator !== BulkPriceDiscriminator.BaseRate) {
                            throw new BusinessError(`"${key}" field was updated for dependent rate`);
                        }

                        acc.price = {
                            discriminator: preparedValues.price.discriminator,
                            value: {
                                ...acc.price?.value,
                                extraOccupancyAdjustment: {
                                    value: preparedValues.price.value.extraOccupancyAdjustment || 0,
                                },
                            },
                        };
                        break;
                    case NEGATIVE_OCCUPANCY_ADJUSTMENT_FIELD_NAME:
                        if (preparedValues?.price?.discriminator !== BulkPriceDiscriminator.BaseRate) {
                            throw new BusinessError(`"${key}" field was updated for dependent rate`);
                        }

                        acc.price = {
                            discriminator: preparedValues.price.discriminator,
                            value: {
                                ...acc.price?.value,
                                negativeOccupancyAdjustment: {
                                    value: preparedValues.price.value.negativeOccupancyAdjustment || 0,
                                },
                            },
                        };
                        break;
                    default: {
                        if (preparedValues?.price?.discriminator !== BulkPriceDiscriminator.DependentRate) {
                            throw new BusinessError(`"${key}" field was updated for base rate`);
                        }

                        const currentValue =
                            acc.price?.discriminator === BulkPriceDiscriminator.DependentRate ? acc.price.value : {};

                        acc.price = {
                            discriminator: preparedValues.price.discriminator,
                            value: {
                                ...currentValue,
                                [key]: {
                                    value: preparedValues.price.value[key],
                                },
                            },
                        };
                    }
                }

                return acc;
            }

            if (isOptionsFieldsKey(key)) {
                acc.options = {
                    ...acc.options,
                    [key]: getEditValue(formValues[key]),
                };

                return acc;
            }

            if (key === ORDERING_FIELD_NAME) {
                acc.ordering = {
                    value: preparedValues.ordering,
                };

                return acc;
            }

            // @ts-expect-error Expression can't be used to index type 'BulkEditRatesValues'
            acc[key] = getEditValue(formValues[key]);

            return acc;
        })
    );

const rateOptionsFields: Array<FieldName> = [
    IS_BONUS_POINTS_ELIGIBLE_FIELD_NAME,
    HIDE_PRICE_FROM_GUEST_FIELD_NAME,
    ENABLED_FIELD_NAME,
];

export const setFieldsConfig = (
    enabledFields: Array<FieldName>,
    lockedFields: Array<FieldName>,
    areRatesDependent: boolean,
    areSomeRatesFromSameEnterprise: boolean
) => {
    const disabledFields = rateOptionsFields.reduce((acc: Array<FieldName>, fieldName) => {
        if (!enabledFields.includes(fieldName)) {
            acc.push(fieldName);
        }

        return acc;
    }, []);

    if (areSomeRatesFromSameEnterprise) {
        disabledFields.push(RATE_KEY_FIELD_NAME);
    }

    if (areRatesDependent) {
        disabledFields.push(BASE_RATE_KEY_FIELD_NAME);
    }

    return {
        disabledFields,
        enabledFields,
        lockedFields,
    };
};
